import { Enumify } from "enumify-ts";
class UnitOfMeasureType extends Enumify() {
    description;
    shortDescription;
    consignmentFreightApplicable;
    vehicleCapacityApplicable;
    vehicleHireApplicable;
    deprecated;
    static QUANTITY_EACH = new UnitOfMeasureType("Qty", "qty", true, false, false);
    static VOLUME_M3 = new UnitOfMeasureType("Cubic Metres", "m3", true, true, true);
    static WEIGHT_TON = new UnitOfMeasureType("Net Tonne", "t", true, true, true);
    static GROSS_TONNE = new UnitOfMeasureType("Gross Tonne", "t", false, false, false);
    static PALLET = new UnitOfMeasureType("Pallet", "plt", true, true, false);
    static BULKA_BAG = new UnitOfMeasureType("Bulka Bag", "BBag", true, false, false);
    static METRES_SQUARED = new UnitOfMeasureType("Square Metres", "m2", true, false, false);
    static HOURS = new UnitOfMeasureType("Hours", "hr", true, false, false);
    static LOADS = new UnitOfMeasureType("Loads", "load", true, false, true);
    static CARTON = new UnitOfMeasureType("Carton", "ctn", true, false, false);
    static ITEM = new UnitOfMeasureType("Item", "itm", true, false, false);
    static CONTAINER = new UnitOfMeasureType("Container", "ctr", true, false, false);
    static CRATE = new UnitOfMeasureType("Crate", "cte", true, false, false);
    static CASK = new UnitOfMeasureType("Cask", "csk", true, false, false);
    static ROLL = new UnitOfMeasureType("Roll", "rl", true, false, false);
    static UNITS = new UnitOfMeasureType("Units", "u", true, false, false, true);
    static LITRES = new UnitOfMeasureType("Litres", "l", true, true, true);
    static GENERAL_FREIGHT_ITEM = new UnitOfMeasureType("General Freight Items", "gf", false, true, false);
    static _ = UnitOfMeasureType._closeEnum();
    constructor(description, shortDescription, consignmentFreightApplicable, vehicleCapacityApplicable, vehicleHireApplicable, deprecated = false) {
        super();
        this.description = description;
        this.shortDescription = shortDescription;
        this.consignmentFreightApplicable = consignmentFreightApplicable;
        this.vehicleCapacityApplicable = vehicleCapacityApplicable;
        this.vehicleHireApplicable = vehicleHireApplicable;
        this.deprecated = deprecated;
    }
    static consignmentFreightSelectOptionsArray(includeGrossTonnes = false) {
        const uoms = UnitOfMeasureType.values
            .filter(uom => uom.isConsignmentFreightApplicable() && !uom.isDeprecated())
            .map(it => {
            return {
                label: it.description,
                value: it.key
            };
        });
        if (includeGrossTonnes) {
            uoms.push({
                label: UnitOfMeasureType.GROSS_TONNE.description,
                value: UnitOfMeasureType.GROSS_TONNE.key
            });
        }
        return uoms;
    }
    /**
     * All non-deprecated units of measure the server accepts for a marketplace product (GROSS_TONNE is UI only)
     */
    static marketplaceProductSelectOptionsArray() {
        return UnitOfMeasureType.values
            .filter(uom => uom !== UnitOfMeasureType.GROSS_TONNE && !uom.isDeprecated())
            .map(it => {
            return {
                label: it.description,
                value: it.key
            };
        });
    }
    static marketplaceProductFiltersArray() {
        return UnitOfMeasureType.values
            .filter(uom => uom !== UnitOfMeasureType.GROSS_TONNE && !uom.isDeprecated())
            .map(it => {
            return { value: it.key, text: it.description };
        });
    }
    static vehicleHireSelectOptionsArray() {
        return UnitOfMeasureType.values
            .filter(uom => uom.isVehicleHireApplicable() && !uom.isDeprecated())
            .map(it => {
            return {
                label: it.description,
                value: it.key
            };
        });
    }
    static vehicleCapacitySelectOptionsArray() {
        return UnitOfMeasureType.values
            .filter(uom => uom.isVehicleCapacityApplicable() && !uom.isDeprecated())
            .map(it => {
            return {
                label: it.description,
                value: it.key
            };
        });
    }
    static enumDescription(uomEnum) {
        if (uomEnum) {
            const uomType = UnitOfMeasureType.valueOf(uomEnum);
            if (uomType) {
                return uomType.description;
            }
            return "?UNKNOWN: " + uomEnum + "?";
        }
        return "";
    }
    static enumShortDescription(uomEnum) {
        if (uomEnum) {
            const uomType = UnitOfMeasureType.valueOf(uomEnum);
            if (uomType) {
                return uomType.shortDescription;
            }
            return "?" + uomEnum + "?";
        }
        return "";
    }
    static longAndShortDescription(uomEnum) {
        if (uomEnum) {
            const uomType = UnitOfMeasureType.valueOf(uomEnum);
            if (uomType) {
                return uomType.description + " (" + uomType.shortDescription + ")";
            }
            return "?UNKNOWN: " + uomEnum + "?";
        }
        return "";
    }
    hasDimensions() {
        // If you change this, change UnitOfMeasureType.java
        switch (this) {
            case UnitOfMeasureType.QUANTITY_EACH:
            case UnitOfMeasureType.PALLET:
            case UnitOfMeasureType.CARTON:
            case UnitOfMeasureType.METRES_SQUARED:
            case UnitOfMeasureType.ITEM:
            case UnitOfMeasureType.CONTAINER:
            case UnitOfMeasureType.CRATE:
            case UnitOfMeasureType.CASK:
            case UnitOfMeasureType.ROLL:
                return true;
            default:
                return false;
        }
    }
    hasGrossTare() {
        // If you change this, change UnitOfMeasureType.java
        switch (this) {
            case UnitOfMeasureType.WEIGHT_TON:
                return true;
            default:
                return false;
        }
    }
    hasNet() {
        // If you change this, change UnitOfMeasureType.java
        switch (this) {
            case UnitOfMeasureType.HOURS:
            case UnitOfMeasureType.LOADS:
            case UnitOfMeasureType.WEIGHT_TON:
                return true;
            default:
                return false;
        }
    }
    isConsignmentFreightApplicable() {
        return this.consignmentFreightApplicable;
    }
    isVehicleCapacityApplicable() {
        return this.vehicleCapacityApplicable;
    }
    isVehicleHireApplicable() {
        return this.vehicleHireApplicable;
    }
    isDeprecated() {
        return this.deprecated || false;
    }
}
export default UnitOfMeasureType;
